import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import SubHeader from '../../layout/components/SubHeader';
import Searchbar from './subparts/Searchbar';
import ContactList from './subparts/ContactList';
import ConversationList from './subparts/ConversationList';
import ContactListSkeleton from '../../smsgateway/pages/messages/widgets/ContactListSkeleton';
import ConversationSkeleton from '../../smsgateway/pages/messages/widgets/ConversationSkeleton';
import ComposePatientModal from '../../smsgateway/pages/messages/widgets/ComposePatientModal';
import { toast } from 'react-toastify';
import smsgatewayService from '../../../services/smsgateway.service';
import clinicService from '../../../services/clinic.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import {
    bumpInboxConversation,
    formatChatTimestamp,
    phonesMatch,
} from '../../../store/smsRealtimeStore';
import useSmsRealtimeSync from '../../../hooks/useSmsRealtimeSync';
import useOutboundDeliveryPolling from '../../../hooks/useOutboundDeliveryPolling';
import '../../smsgateway/layout/SmsSkeleton.css';
import './Messages.css';

const splitCustomerNames = (customerNames) => {
    const parts = String(customerNames || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return { firstName: '', lastName: '' };
    }
    if (parts.length === 1) {
        return { firstName: parts[0], lastName: '' };
    }
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
};

const buildConversationStub = ({ phone, firstName = '', lastName = '', dob = '' }) => ([
    {
        customer: phone,
        customerFirstName: firstName,
        customerLastName: lastName,
        customerDob: dob,
    },
]);

const getContactDetails = (chatMessages) => {
    if (!Array.isArray(chatMessages) || chatMessages.length === 0) {
        return null;
    }

    return chatMessages.find((msg) => msg?.customerFirstName || msg?.customerLastName) || chatMessages[0];
};

const normalizeCustomerDob = (dob) => {
    if (!dob) {
        return null;
    }

    if (Array.isArray(dob) && dob.length >= 3) {
        const [year, month, day] = dob;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    return dob;
};

const buildSendMessagePayload = ({
    clinicId,
    clinicSms,
    customer,
    message,
    contactMessage,
    isConfirmationRequired,
}) => {
    const payload = {
        clinicId,
        business: clinicSms,
        customer,
        message,
        isConfirmationRequired,
    };

    if (contactMessage?.customerFirstName) {
        payload.customerFirstName = contactMessage.customerFirstName;
    }
    if (contactMessage?.customerLastName) {
        payload.customerLastName = contactMessage.customerLastName;
    }

    const customerDob = normalizeCustomerDob(contactMessage?.customerDob);
    if (customerDob) {
        payload.customerDob = customerDob;
    }

    return payload;
};

function Messages() {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const pendingCustomer = useRef(location.state?.customer);
    const usersRef = useRef([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchCriteria, setSearchCriteria] = useState('firstName');
    const [sortOrder, setSortOrder] = useState(
        location.state?.initialSort || searchParams.get('sort') || 'desc'
    );
    const [activeSearchString, setActiveSearchString] = useState('');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [hasNext, setHasNext] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [chat, setChat] = useState([]);
    const [loadConversation, setLoadConversation] = useState(false);
    const [conversationDetails, setConversationDetails] = useState([]);
    const [conversationDetailsLoading, setConversationDetailsLoading] = useState(false);
    const [isLegacyTwoWayText, setIsLegacyTwoWayText] = useState(false);
    const [showComposeModal, setShowComposeModal] = useState(false);
    const isMounted = useRef(false);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const clinicType = useEyeconxStore((state) => state.user?.clinicType);
    const clinicInfo = useEyeconxStore((state) => state.clinicInfo);
    const setClinicInfo = useEyeconxStore((state) => state.setClinicInfo);
    const isEyeconxClinic = clinicType === 'EYECONX';
    const canCompose = isEyeconxClinic || isLegacyTwoWayText;
    const composePatientSource = isEyeconxClinic ? 'eyeconx' : 'legacy';

    usersRef.current = users;

    const getUserList = useCallback((cursor = null, size = 10, searchString = activeSearchString, sort = sortOrder, { quiet = false } = {}) => {
        if (!quiet) {
            if (cursor === null) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
        }

        smsgatewayService.getInbox(clinicId, searchCriteria, searchString, cursor, size, sort)
            .then((res) => {
                if (cursor === null) {
                    setUsers(res.data.data);
                } else {
                    setUsers((prevUser) => [...prevUser, ...res.data.data]);
                }
                setNextCursor(res.data.nextCursor);
                setHasNext(res.data.hasNext);
                setLoading(false);
                setLoadingMore(false);
            })
            .catch(() => {
                if (!quiet) {
                    toast.error('Error getting Users');
                }
                setLoading(false);
                setLoadingMore(false);
            });
    }, [clinicId, searchCriteria, activeSearchString, sortOrder]);

    const resolveConversationStub = useCallback((phone, overrides = {}) => {
        const inboxRow = usersRef.current.find((u) => phonesMatch(u.customerNumber, phone));
        const { firstName, lastName } = splitCustomerNames(inboxRow?.customerNames);
        return buildConversationStub({
            phone,
            firstName: overrides.firstName ?? firstName,
            lastName: overrides.lastName ?? lastName,
            dob: overrides.dob ?? '',
        });
    }, []);

    const handleConversation = useCallback((user, pageNo = 0, pageSize = 30, { showLoader = true, stubOverrides } = {}) => {
        if (showLoader) {
            setLoadConversation(true);
        }
        smsgatewayService.getConversation(clinicId, user, pageNo, pageSize)
            .then((res) => {
                if (Array.isArray(res.data) && res.data.length > 0) {
                    setChat(res.data);
                } else {
                    setChat(resolveConversationStub(user, stubOverrides));
                }
                setLoadConversation(false);
            })
            .catch((err) => {
                if (err?.response?.status === 404) {
                    setChat(resolveConversationStub(user, stubOverrides));
                } else if (showLoader) {
                    toast.error('Error getting Conversation');
                }
                setLoadConversation(false);
            });
    }, [clinicId, resolveConversationStub]);

    const handleUserSelect = useCallback((user) => {
        setSelectedUser(user);
        setUsers((prevUsers) => prevUsers.map((row) => (
            phonesMatch(row.customerNumber, user)
                ? { ...row, unreadMessage: 0 }
                : row
        )));
        handleConversation(user);
    }, [handleConversation]);

    const handleRealtimeReconnect = useCallback(() => {
        getUserList(null, 10, activeSearchString, sortOrder, { quiet: true });
        if (selectedUser) {
            handleConversation(selectedUser, 0, 30, { showLoader: false });
        }
    }, [getUserList, activeSearchString, sortOrder, selectedUser, handleConversation]);

    useSmsRealtimeSync({
        selectedUser,
        setChat,
        setUsers,
        onReconnect: handleRealtimeReconnect,
    });

    useOutboundDeliveryPolling({
        clinicId,
        selectedUser,
        chat,
        setChat,
    });

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            getUserList();
        }
    }, [getUserList]);

    useEffect(() => {
        if (!clinicId) return;

        smsgatewayService.getClinicCustomize(clinicId)
            .then((res) => {
                const legacySetting = (res.data || []).find((item) => item.optionKey === 'isLegacyTwoWayText');
                setIsLegacyTwoWayText(legacySetting?.optionValue === '1');
            })
            .catch(() => {
                setIsLegacyTwoWayText(false);
            });
    }, [clinicId]);

    useEffect(() => {
        if (!clinicId) return;

        // Always refresh clinic SMS number so DB/admin changes are not stuck behind persisted store cache.
        clinicService.getClinicInfo(clinicId)
            .then((res) => {
                setClinicInfo(res.data);
            })
            .catch(() => {
                toast.error('Error loading clinic SMS settings');
            });
    }, [clinicId, setClinicInfo]);

    useEffect(() => {
        if (pendingCustomer.current) {
            handleUserSelect(pendingCustomer.current);
            pendingCustomer.current = null;
            window.history.replaceState({}, document.title);
        }
    }, [handleUserSelect]);

    const handleRefresh = () => {
        setSelectedUser(null);
        setConversationDetails(null);
        setUsers([]);
        setNextCursor(null);
        setHasNext(false);
        getUserList(null, 10, activeSearchString, sortOrder);
    };

    const handleDeleteConversation = () => {
        if (!selectedUser) return;

        smsgatewayService.deleteConversation(clinicId, selectedUser)
            .then(() => {
                toast.success('Conversation deleted successfully');
                handleRefresh();
            })
            .catch(() => {
                toast.error('Error deleting conversation');
            });
    };

    const handleSearchChange = (strSearch) => {
        setActiveSearchString(strSearch);
        setUsers([]);
        setNextCursor(null);
        setHasNext(false);
        getUserList(null, 10, strSearch, sortOrder);
    };

    const handleSortChange = (newSort, searchString = activeSearchString) => {
        setSortOrder(newSort);
        setActiveSearchString(searchString);
        setUsers([]);
        setNextCursor(null);
        setHasNext(false);
        getUserList(null, 10, searchString, newSort);
    };

    const handleSearchCriteriaChange = (e) => {
        setSearchCriteria(e.target.value);
    };

    const handleReset = () => {
        setSearchCriteria('firstName');
        setSearchTerm('');
        setActiveSearchString('');
        setUsers([]);
        setNextCursor(null);
        setHasNext(false);
        getUserList(null, 10, '', sortOrder);
    };

    const handleLoadMore = () => {
        if (hasNext && !loadingMore) {
            getUserList(nextCursor, 10, activeSearchString, sortOrder);
        }
    };

    const handleSendMessage = (message, isConfirmationRequired = false) => {
        if (message.trim() === '' || !selectedUser) return;

        const clinicSms = clinicInfo?.sms
            || chat.find((msg) => msg?.business)?.business
            || null;

        if (!clinicSms) {
            toast.error('Clinic SMS number is not available yet. Please try again.');
            return;
        }

        const contactMessage = getContactDetails(chat);
        const customer = contactMessage?.customer || selectedUser;
        const sendNewMessage = buildSendMessagePayload({
            clinicId,
            clinicSms,
            customer,
            message,
            contactMessage,
            isConfirmationRequired,
        });

        const clientMessageId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const pendingMessage = {
            id: null,
            clientMessageId,
            business: clinicSms,
            customer: selectedUser,
            message,
            customerFirstName: contactMessage?.customerFirstName || '',
            customerLastName: contactMessage?.customerLastName || '',
            customerDob: contactMessage?.customerDob || '',
            messageType: 'OUTGOING',
            status: 'PENDING',
            modifiedAt: formatChatTimestamp(),
        };
        setChat((prevChat) => {
            const base = Array.isArray(prevChat) ? prevChat.filter((m) => m && m.message) : [];
            return [...base, pendingMessage];
        });
        setUsers((prevUsers) => bumpInboxConversation(prevUsers, {
            customer: selectedUser,
            lastMessage: message,
            customerFirstName: contactMessage?.customerFirstName,
            customerLastName: contactMessage?.customerLastName,
        }));

        smsgatewayService.sendMessage(clinicId, sendNewMessage)
            .then((res) => {
                const serverId = res.data?.id ?? null;
                const serverStatus = res.data?.status || 'ACCEPTED';
                const finalMessage = res.data?.message ?? message;
                setChat((prevChat) => prevChat.map((msg) => {
                    if (msg.clientMessageId !== clientMessageId) {
                        return msg;
                    }
                    return {
                        ...msg,
                        id: serverId,
                        message: finalMessage,
                        status: serverStatus,
                        clientMessageId: undefined,
                    };
                }));
                setUsers((prevUsers) => bumpInboxConversation(prevUsers, {
                    customer: selectedUser,
                    lastMessage: finalMessage,
                    customerFirstName: contactMessage?.customerFirstName,
                    customerLastName: contactMessage?.customerLastName,
                }));
            })
            .catch(() => {
                setChat((prevChat) => prevChat.map((msg) =>
                    msg.clientMessageId === clientMessageId
                        ? { ...msg, status: 'FAILED' }
                        : msg
                ));
                toast.error('Error sending Message');
            });
    };

    const getConversationDetails = () => {
        setConversationDetails(null);
        setConversationDetailsLoading(true);
        smsgatewayService.getConversationDetails(clinicId, selectedUser)
            .then((res) => {
                setConversationDetails(res.data);
                setConversationDetailsLoading(false);
            })
            .catch(() => {
                toast.error('Error getting Conversation Details');
                setConversationDetailsLoading(false);
            });
    };

    const handleComposePatientSelect = (patient) => {
        const phone = patient.phone;
        const stubChat = buildConversationStub({
            phone,
            firstName: patient.firstName,
            lastName: patient.lastName,
            dob: patient.dob,
        });

        setShowComposeModal(false);
        setSelectedUser(phone);
        setChat(stubChat);
        setLoadConversation(true);

        const firstName = patient.firstName?.trim();
        const lastName = patient.lastName?.trim();
        const nameBackfill = (firstName || lastName)
            ? smsgatewayService.updateCustomerName(clinicId, phone, {
                ...(firstName ? { customerFirstName: firstName } : {}),
                ...(lastName ? { customerLastName: lastName } : {}),
            }).catch(() => null)
            : Promise.resolve(null);

        nameBackfill
            .then(() => smsgatewayService.getConversation(clinicId, phone, 0, 30))
            .then((res) => {
                if (res?.data && res.data.length > 0) {
                    setChat(res.data.map((msg) => ({
                        ...msg,
                        customerFirstName: msg.customerFirstName || patient.firstName,
                        customerLastName: msg.customerLastName || patient.lastName,
                        customerDob: msg.customerDob || patient.dob,
                    })));
                } else {
                    setChat(stubChat);
                }
            })
            .catch(() => {
                setChat(stubChat);
            })
            .finally(() => {
                setLoadConversation(false);
            });
    };

    return (
        <div className="ecx-page messages-page">
            <SubHeader
                title="Messages"
                description="Communicate with patients using SMS"
                icon="bi-chat-left-text"
                containerClass="container-fluid px-4"
            >
                <div className="d-flex align-items-center gap-2">
                    {canCompose && (
                        <button
                            className="btn btn-light-primary btn-sm rounded-pill"
                            onClick={() => setShowComposeModal(true)}
                            title="Compose new message"
                        >
                            <i className="bi bi-pencil-square me-1"></i>
                            Compose
                        </button>
                    )}
                    <button
                        className="btn btn-light-primary btn-sm rounded-pill"
                        onClick={handleRefresh}
                        title="Refresh messages"
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                    </button>
                </div>
            </SubHeader>
            <ComposePatientModal
                show={showComposeModal}
                onHide={() => setShowComposeModal(false)}
                clinicId={clinicId}
                patientSource={composePatientSource}
                onPatientSelect={handleComposePatientSelect}
            />
            <div className="container-fluid px-4 py-3">
                <div className="row g-3">
                    <div className="col-12 col-lg-4 col-xl-4">
                        <div className="card shadow-sm border-0" style={{ height: 'calc(100vh - 200px)' }}>
                            <div className="card-body p-3 d-flex flex-column h-100" style={{ overflow: 'hidden' }}>
                                <Searchbar
                                    handleSearchChange={handleSearchChange}
                                    searchCriteria={searchCriteria}
                                    handleSearchCriteriaChange={handleSearchCriteriaChange}
                                    searchTerm={searchTerm}
                                    handleReset={handleReset}
                                    sortOrder={sortOrder}
                                    handleSortChange={handleSortChange}
                                />
                                {loading ? (
                                    <ContactListSkeleton />
                                ) : (
                                    <ContactList
                                        users={users}
                                        handleUserSelect={handleUserSelect}
                                        selectedUser={selectedUser}
                                        handleLoadMore={handleLoadMore}
                                        hasNext={hasNext}
                                        loadingMore={loadingMore}
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-lg-8 col-xl-8">
                        {selectedUser ? (
                            <>
                                {loadConversation ? (
                                    <ConversationSkeleton />
                                ) : (
                                    Array.isArray(chat) && chat.length > 0 && (
                                        <ConversationList
                                            smsData={chat}
                                            handleSendMessage={handleSendMessage}
                                            getConversationDetails={getConversationDetails}
                                            conversationDetails={conversationDetails}
                                            conversationDetailsLoading={conversationDetailsLoading}
                                            handleDeleteConversation={handleDeleteConversation}
                                        />
                                    )
                                )}
                            </>
                        ) : (
                            <div className="card shadow-sm border-0" style={{ height: 'calc(100vh - 200px)' }}>
                                <div className="card-body d-flex flex-column justify-content-center align-items-center text-center">
                                    <div className="bg-light rounded-circle p-4 mb-4">
                                        <i className="bi bi-chat-square-text text-primary" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                    <h5 className="text-dark fw-normal mb-2">Select a conversation</h5>
                                    <p className="text-muted mb-0">Choose a contact from the list to start messaging</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Messages;
